import { ActionContext } from 'vuex'
import { Tour, TourState } from '../models/interfaces'
import { MutationTypes } from './mutations'

/**
 * Tipos de acciones para el módulo de tour
 */
export enum ActionTypes {
  START_TOUR = 'startTour',
  NEXT_STEP = 'nextStep',
  PREV_STEP = 'prevStep',
  END_TOUR = 'endTour',
  SKIP_TOUR = 'skipTour',
  GO_TO_STEP = 'goToStep',
  TOGGLE_TOUR_ENABLED = 'toggleTourEnabled',
}

type Context = ActionContext<TourState, any>;

/**
 * Acciones del módulo de tour
 */
export const actions = {
  /**
   * Inicia un tour específico
   */
  async [ActionTypes.START_TOUR] ({ commit, state }: Context, tour: Tour) {
    // Verificar si los tours están habilitados globalmente
    if (!state.isTourEnabled) return

    // Reiniciar el tour actual si existe
    commit(MutationTypes.RESET_TOUR)

    // Configurar el nuevo tour
    commit(MutationTypes.SET_ACTIVE_TOUR, tour)
    commit(MutationTypes.SET_CURRENT_STEP, 0)
    commit(MutationTypes.SET_IS_ACTIVE, true)

    // Ejecutar la función beforeStep del primer paso si existe
    const firstStep = tour.steps[0]
    if (firstStep && firstStep.beforeStep) {
      await firstStep.beforeStep()
    }

    // Ejecutar la acción del primer paso si existe
    if (firstStep && firstStep.action) {
      firstStep.action()
    }
  },

  /**
   * Avanza al siguiente paso del tour
   */
  async [ActionTypes.NEXT_STEP] ({ commit, state, dispatch }: Context) {
    if (!state.activeTour || !state.isActive) return

    const currentStep = state.activeTour.steps[state.currentStepIndex]
    // Ejecutar afterStep del paso actual si existe
    if (currentStep && currentStep.afterStep) {
      await currentStep.afterStep()
    }

    const nextIndex = state.currentStepIndex + 1
    // Verificar si es el último paso
    if (nextIndex >= state.activeTour.steps.length) {
      return dispatch(ActionTypes.END_TOUR)
    }

    commit(MutationTypes.SET_CURRENT_STEP, nextIndex)

    // Ejecutar beforeStep del nuevo paso si existe
    const nextStep = state.activeTour.steps[nextIndex]
    if (nextStep && nextStep.beforeStep) {
      await nextStep.beforeStep()
    }

    // Ejecutar acción del nuevo paso si existe
    if (nextStep && nextStep.action) {
      nextStep.action()
    }
  },

  /**
   * Retrocede al paso anterior del tour
   */
  async [ActionTypes.PREV_STEP] ({ commit, state }: Context) {
    if (!state.activeTour || !state.isActive || state.currentStepIndex <= 0) return

    const currentStep = state.activeTour.steps[state.currentStepIndex]
    // Ejecutar afterStep del paso actual si existe (para limpieza)
    if (currentStep && currentStep.afterStep) {
      await currentStep.afterStep()
    }

    const prevIndex = state.currentStepIndex - 1
    commit(MutationTypes.SET_CURRENT_STEP, prevIndex)

    // Ejecutar beforeStep del paso anterior si existe
    const prevStep = state.activeTour.steps[prevIndex]
    if (prevStep && prevStep.beforeStep) {
      await prevStep.beforeStep()
    }

    // Ejecutar acción del paso anterior si existe
    if (prevStep && prevStep.action) {
      prevStep.action()
    }
  },

  /**
   * Finaliza el tour actual (completado)
   */
  [ActionTypes.END_TOUR] ({ commit, state }: Context) {
    if (!state.activeTour || !state.isActive) return

    // Ejecutar el callback de completado si existe
    if (state.activeTour.onComplete) {
      state.activeTour.onComplete()
    }

    // Añadir el tour al historial
    commit(MutationTypes.ADD_TO_HISTORY, state.activeTour.id)

    // Reiniciar el estado del tour
    commit(MutationTypes.RESET_TOUR)
  },

  /**
   * Omite el tour actual (no completado)
   */
  [ActionTypes.SKIP_TOUR] ({ commit, state }: Context) {
    if (!state.activeTour || !state.isActive) return

    // Ejecutar el callback de omisión si existe
    if (state.activeTour.onSkip) {
      state.activeTour.onSkip()
    }

    // Reiniciar el estado del tour sin añadirlo al historial
    commit(MutationTypes.RESET_TOUR)
  },

  /**
   * Navega a un paso específico del tour
   */
  async [ActionTypes.GO_TO_STEP] ({ commit, state }: Context, stepIndex: number) {
    if (!state.activeTour || !state.isActive) return
    if (stepIndex < 0 || stepIndex >= state.activeTour.steps.length) return

    // Ejecutar afterStep del paso actual si existe
    const currentStep = state.activeTour.steps[state.currentStepIndex]
    if (currentStep && currentStep.afterStep) {
      await currentStep.afterStep()
    }

    commit(MutationTypes.SET_CURRENT_STEP, stepIndex)

    // Ejecutar beforeStep del nuevo paso si existe
    const newStep = state.activeTour.steps[stepIndex]
    if (newStep && newStep.beforeStep) {
      await newStep.beforeStep()
    }

    // Ejecutar acción del nuevo paso si existe
    if (newStep && newStep.action) {
      newStep.action()
    }
  },

  /**
   * Activa o desactiva los tours globalmente
   */
  [ActionTypes.TOGGLE_TOUR_ENABLED] ({ commit }: Context, enabled: boolean) {
    commit(MutationTypes.SET_TOUR_ENABLED, enabled)
  },
}
